import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  listInventoryDuplicates,
  listInventoryMerges,
  undoInventoryMerge,
  type InventoryDuplicateGroup,
} from '@/lib/api/inventory-merge.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { MergeInventoryModal } from '@/components/inventory/MergeInventoryModal.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';

export function InventoryDuplicatesPage() {
  const queryClient = useQueryClient();
  const { requireOverride, modalProps } = useManagerOverride();
  const [group, setGroup] = useState<InventoryDuplicateGroup | null>(null);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['inventory-duplicates', page],
    queryFn: () => listInventoryDuplicates({ page, pageSize: 20 }),
  });
  const mergesQuery = useQuery({
    queryKey: ['inventory-merges'],
    queryFn: listInventoryMerges,
  });

  const undoMutation = useMutation({
    mutationFn: async (id: string) => {
      const pin = await requireOverride({
        action: 'inventory.merge.undo',
        title: 'Undo merge',
        description: 'This restores the original items, stock layers, and references.',
        requiredRole: 'store_manager',
      });
      if (!pin.approved) throw new Error('cancelled');
      return undoInventoryMerge(id, pin.authorizedBy.id);
    },
    onSuccess: () => {
      toastSuccess('Merge undone');
      void queryClient.invalidateQueries({ queryKey: ['inventory-duplicates'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory-merges'] });
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'cancelled') return;
      toastError(parseApiError(error).message);
    },
  });

  const groups = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Inventory duplicates</h1>
        <p className="mt-1 text-sm text-slate-500">
          Suggestions only. A matching name is not proof these are the same part — soft OLED and incell are different grades.
          Nothing is merged until you review it.{' '}
          <Link to="/settings/data-cleanup/balances" className="font-semibold text-primary hover:underline">
            Balance cleanup
          </Link>
        </p>
      </div>

      {query.isLoading ? <p className="text-sm text-slate-500">Looking for possible duplicates…</p> : null}

      <div className="space-y-4">
        {groups.map((row) => (
          <article key={row.groupId} className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            {row.gradeWarning ? (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <p className="font-semibold">
                  These may be different GRADES, not different suppliers. Merging will lose grade distinction.
                </p>
                <p>{row.gradeReasons.join(' ')}</p>
              </div>
            ) : null}
            <div className="flex items-center justify-between px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
              <span>{prettyLabel(row.strategy)} · {row.confidence} confidence</span>
              <span>{row.matchedOn}</span>
            </div>
            <ul className="divide-y">
              {row.items.map((item) => (
                <li key={item.id} className="px-4 py-3 text-sm">
                  <div className="font-medium text-slate-950">{item.name}</div>
                  <div className="text-slate-600">
                    {item.sku ? `SKU ${item.sku}` : 'No SKU'} · {formatCurrency(item.costPrice)} · qty {item.qtyOnHand}
                    {item.condition ? ` · ${prettyLabel(item.condition)}` : ''}
                  </div>
                  <div className="text-slate-500">
                    {item.suppliers.length
                      ? item.suppliers.map((supplier) => supplier.supplierName).join(', ')
                      : 'No supplier assigned'}
                    {item.suggestedSupplier ? ` · suggested ${item.suggestedSupplier.supplierName}` : ''}
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex justify-end border-t px-4 py-3">
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={() => setGroup(row)}
              >
                Review & merge
              </button>
            </div>
          </article>
        ))}
        {!query.isLoading && groups.length === 0 ? (
          <p className="rounded-2xl border bg-white px-4 py-8 text-center text-sm text-slate-500">
            No duplicate groups at this confidence. You can still merge two items from the item menu.
          </p>
        ) : null}
      </div>

      {total > 20 ? (
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-xl border px-3 py-1 text-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <button type="button" className="rounded-xl border px-3 py-1 text-sm" disabled={page * 20 >= total} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      ) : null}

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <h2 className="font-semibold text-slate-950">Recent merges</h2>
        <ul className="mt-2 divide-y text-sm">
          {(mergesQuery.data ?? []).map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3 py-2">
              <div>
                <div>{row.names.join(' + ') || 'Inventory merge'}</div>
                <div className="text-xs text-slate-500">{row.reason}{row.reversedAt ? ' · undone' : ''}</div>
              </div>
              {!row.reversedAt ? (
                <button type="button" className="text-sm font-semibold text-primary" onClick={() => undoMutation.mutate(row.id)}>
                  Undo
                </button>
              ) : null}
            </li>
          ))}
          {(mergesQuery.data ?? []).length === 0 ? <li className="py-2 text-slate-500">No merges yet.</li> : null}
        </ul>
      </section>

      {group ? (
        <MergeInventoryModal
          open
          items={group.items}
          gradeReasons={group.gradeReasons}
          onClose={() => setGroup(null)}
          onMerged={() => {
            setGroup(null);
            void queryClient.invalidateQueries({ queryKey: ['inventory-duplicates'] });
            void queryClient.invalidateQueries({ queryKey: ['inventory-merges'] });
            void queryClient.invalidateQueries({ queryKey: ['inventory'] });
          }}
        />
      ) : null}
      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </div>
  );
}
